import {dissocPath, omit} from 'ramda'

import {getDeviceCategory} from '../../../_web/utils/userAgent'
import {getConnection, getUserTier} from '../../../auth/modules/selectors'
import {User} from '../../../auth/types/Cognito'
import {brandCodes} from '../../../brand/constants'
import {Brand} from '../../../brand/types'
import {getDialInCodeAdaptedToSmsGateway} from '../../../localization/business/countryDialCodes'
import {Action, Category, Entity} from '../../types/Events'
import {getCio} from './getCio'
import {BookingContactDetails, EventPayload, TrackEvent} from './types'

const deviceInfo = () => ({
  deviceType: getDeviceCategory(),
  userAgent: navigator.userAgent
})

const getBookingPhoneNumber = (phone?: BookingContactDetails['phone']) => {
  if (!phone) return
  const countryCallingCode = getDialInCodeAdaptedToSmsGateway(phone.countryCode)
  return `${countryCallingCode}${phone.number}`
}

const formatUserContactDetails = (
  bookingContactDetails?: BookingContactDetails | null,
  user?: User | null
) => {
  const {attributes} = user || {}

  if (!bookingContactDetails && !user) return

  return {
    firstName: attributes?.given_name || bookingContactDetails?.firstName,
    lastName: attributes?.family_name || bookingContactDetails?.lastName,
    phone:
      attributes?.phone_number ||
      getBookingPhoneNumber(bookingContactDetails?.phone),
    email: attributes?.email || bookingContactDetails?.email,
    bookingFirstName: bookingContactDetails?.firstName,
    bookingLastName: bookingContactDetails?.lastName,
    bookingEmail: bookingContactDetails?.email,
    bookingPhone: getBookingPhoneNumber(bookingContactDetails?.phone)
  }
}

/**
 * Adds or updates a user in Customer.io with their details
 * @param params - Object containing user information, contact details, and other tracking data
 * @param params.user - User object containing user attributes
 * @param params.country - Country code of the user
 * @param params.language - Language preference of the user
 * @param params.anonymousId - Anonymous identifier for the user
 * @param params.bookingContactDetails - Contact details provided during booking
 * @param params.timezone - User's timezone
 */
const addOrUpdateUser: TrackEvent = ({
  user,
  country,
  language,
  anonymousId,
  bookingContactDetails,
  timezone
}) => {
  if (!user && !bookingContactDetails) return

  const userId = user?.attributes?.sub || bookingContactDetails?.email
  const authenticationMethod = user && getConnection(user)
  const tier = getUserTier(user)
  const userContactDetails = formatUserContactDetails(
    bookingContactDetails,
    user
  )

  const event: EventPayload = {
    ...(tier && {tier}),
    ...(country && {country}),
    ...(language && {language}),
    ...(timezone && {timezone}),
    ...(authenticationMethod && {authenticationMethod}),
    ...userContactDetails,
    ...deviceInfo()
  }

  getCio()?.identify({
    anonymous_id: anonymousId,
    id: userId,
    ...event
  })
}

/**
 * Formats a given date (in milliseconds since the Unix Epoch) according to the specified locale.
 * @param locale - A locale string (e.g., 'en-US', 'fr-FR') to format the date.
 * @param date - A date in milliseconds since the Unix Epoch (January 1, 1970).
 * @returns - The formatted date string according to the specified locale.
 * @example
 * console.log(toLocale('en-US', 1672531199000)); // Output: "Dec 31, 2022"
 * console.log(toLocale('fr-FR', 1672531199000)); // Output: "31 déc. 2022"
 * console.log(toLocale('ja-JP', 1672531199000)); // Output: "2022年12月31日"
 */
const toLocale = (locale: string, date: number) =>
  new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  })

/**
 * Adds check-in and check-out date strings formatted according to locale
 * @param payload - Event payload containing check-in and check-out timestamps
 * @returns Updated payload with formatted date strings
 */
export const addCheckInCheckOutString = (payload: EventPayload) => {
  const {checkIn, checkOut, locale} = payload || {}
  if (!checkIn || !checkOut || !locale) return payload

  return {
    ...payload,
    checkInString: toLocale(locale, checkIn * 1000),
    checkOutString: toLocale(locale, checkOut * 1000)
  }
}

/**
 * Tracks an event in Customer.io with the provided event data
 * @param event - Object containing event name and associated data
 */
const trackEvent: TrackEvent = ({...event}) => {
  const eventName = event?.name as string
  const payload = omit(['name'], event)

  const augmentedPayload = addCheckInCheckOutString(payload)

  if (payload?.userId) {
    getCio()?.track(eventName, {...augmentedPayload})
    return
  }

  const userContactDetails =
    formatUserContactDetails(augmentedPayload?.bookingContactDetails) || {}

  const removeDuplicatedBookingContactDetails = dissocPath<EventPayload>(
    ['bookingContactDetails'],
    augmentedPayload
  )

  getCio()?.track(eventName, {
    ...removeDuplicatedBookingContactDetails,
    ...userContactDetails,
    ...deviceInfo()
  })
}

/**
 * Returns the standardized page view event name for Customer.io tracking
 * @returns Formatted page view event name string
 */
export const cioTrackPageViewName = (): string =>
  `${Category.System}_${Entity.Page}_${Action.Displayed}`

/**
 * Tracks a page view event in Customer.io
 * @param params - Object containing tracking parameters
 */
const trackPageView: TrackEvent = params =>
  trackEvent({
    ...params,
    name: cioTrackPageViewName()
  })

const emptyFn: () => void = () => undefined

const emptyCio = {
  addOrUpdateUser: emptyFn,
  trackEvent: emptyFn,
  trackPageView: emptyFn
}

export interface CustomerIoFunctions {
  addOrUpdateUser: TrackEvent
  trackEvent: TrackEvent
  trackPageView: TrackEvent
}

/**
 * Creates Customer.io tracking functions for a specific brand
 * @param brand - The brand object to create tracking functions for
 * @returns Object containing Customer.io tracking functions
 */
export const customerIo = (brand?: Brand): CustomerIoFunctions => {
  if (!brand || !brandCodes.includes(brand.code)) {
    return emptyCio
  }

  const withBrand =
    (fn: TrackEvent): TrackEvent =>
    (params: EventPayload) => {
      return fn({
        ...params,
        brand: brand.code
      })
    }

  return {
    addOrUpdateUser: withBrand(addOrUpdateUser),
    trackEvent: withBrand(trackEvent),
    trackPageView: withBrand(trackPageView)
  }
}
